import React from "react";
import {
	Button,
	Card,
	CardHeader,
	CardBody,
	CardTitle,
	Row,
	Col,
	Input,
	FormGroup,
	Label
} from "reactstrap";
import Datetime from "react-datetime";
import moment from "moment";
import Select from 'react-select';

// Requests
import { getCommissionRequest, updateCommissionRequest } from "api/commissions";
import { getClientsRequest } from "api/client";
import { getSellersRequest } from "api/seller";
import { getBrokersRequest } from "api/broker";
// Helpers
import { getFormattedDate } from "helpers/date";
import { formatPaymentStatus } from "helpers/status";
// Constants
import { paymentStatuses } from "../../constants";
//Components
import CurrencyInput from "components/CurrencyInput/CurrencyInput";



class CommissionForm extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			commission: null,
			error: null,
			saving: false,
			filtersClients: {},
			filtersBrokers: {},
			filtersSellers: {}
		}
		this.timeout = null;
	}

    goTo = path => this.props.history.push(path);

	async componentDidMount() {
		const params = new URLSearchParams(this.props.location.search);
		const id = params.get('id');

		if (id) {
			try {
				const commission = await getCommissionRequest(id);

				// Set up client select
				if(commission.client) {
					commission.selectedClient = {
						label: `${commission.client.first_name} ${commission.client.last_name}`,
						value: commission.client._id
					};
				}

				// Set up status select
				if(commission.status) {
					commission.selectedStatus = {
						label: `${formatPaymentStatus(commission.status)}`,
						value: commission.status
					}
				}

				// Set up recipient type select
				if(commission.recipientType) {
					commission.selectedRecipientType = {
						label: commission.recipientType.charAt(0).toUpperCase() + commission.recipientType.slice(1),
						value: commission.recipientType
					}
				}

				// Set up recipient (broker or seller) select
				if(commission.recipientId) {
					if(commission.recipientType === 'broker') {
						commission.selectedRecipient = {
							label: commission.recipientId.name,
							value: commission.recipientId._id
						};
					} else if(commission.recipientType === 'seller') {
						commission.selectedRecipient = {
							label: `${commission.recipientId.first_name} ${commission.recipientId.last_name}`,
							value: commission.recipientId._id
						};
					}
				}

				this.setState({ commission })
			} catch(err) {
				this.setState({
					error: err.message || 'There was an error fetching commission'
				});
			}
		} else {
			// Redirect back since we don't allow creating new commissions
			this.goTo('/commissions');
		}
	}

	onChangeField = (e) => {
		const { target } = e;
		const { name, value } = target;
		this.setState(prevState => ({
			commission: {
				...prevState.commission,
				[name]: value,
			},
		}));
	}

	onSelectChange = (key) => (object) => {
		this.setState(prevState => ({
			commission: {
				...prevState.commission,
				[key]: object,
			},
		}));
	}

	save = async () => {
		this.setState({
			saving: true,
			error: null
		})

		const { commission } = this.state;
		const { _id } = commission;

		// Validation
		if(!_id) {
			return this.setState({
				error: 'Cannot create new commissions through this form',
				saving: false
			});
		}

		if(!commission.amount) {
			return this.setState({
				error: 'Amount is required',
				saving: false
			});
		}

		if(!commission.selectedStatus) {
			return this.setState({
				error: 'Status is required',
				saving: false
			});
		}

		if(!commission.due_date) {
			return this.setState({
				error: 'Due date is required',
				saving: false
			});
		}

		// Prepare commission data for update
		const commissionData = {
			_id: commission._id,
			amount: commission.amount,
			description: commission.description,
			status: commission.selectedStatus.value,
			due_date: commission.due_date,
			settled_date: commission.settled_date,
			check_number: commission.check_number,
			date_received: commission.date_received,
			or_number: commission.or_number
		};

		try {
			await updateCommissionRequest(commissionData);
			this.setState({
				saving: false
			})
			this.goTo('/commissions');
		} catch (err) {
			console.log(err, "error");
			this.setState({
				error: err.message || 'There was an error saving commission',
				saving: false
			})
		}
	}

	loadClients = async (search, callback) => {
		const { filtersClients } = this.state;
		this.setState({
			filtersClients: {
				...filtersClients,
				search
			}
		})
		if(search) {
			const clientResponse = await getClientsRequest(filtersClients);
			const clients = clientResponse.clients.map(client => {
				return {
					data: client,
					label: `${client.first_name} ${client.last_name}`,
					value: client._id
				}
			})
			callback(clients);
		}
	}

	loadBrokers = async (search, callback) => {
		const { filtersBrokers } = this.state;
		this.setState({
			filtersBrokers: {
				...filtersBrokers,
				search
			}
		})
		if(search) {
			const brokerResponse = await getBrokersRequest(filtersBrokers);
			const brokers = brokerResponse.brokers.map(broker => {
				return {
					data: broker,
					label: broker.name,
					value: broker._id
				}
			})
			callback(brokers);
		}
	};

    loadSellers = async (search, callback) => {
		const { filtersSellers } = this.state;
		this.setState({
			filtersSellers: {
				...filtersSellers,
				search
			}
		})
		if(search) {
			const sellerResponse = await getSellersRequest(filtersSellers);
			const sellers = sellerResponse.sellers.map(seller => {
				return {
					data: seller,
					label: `${seller.first_name} ${seller.last_name}`,
					value: seller._id
				}
			})
			callback(sellers);
		}
	};

    render() {

		const {
			commission = {},
			error = null,
			saving
		} = this.state;

		const {
			_id,
			selectedStatus = null,
			commissionable_amount,
			cleared_percentage,
			vat_amount,
			withholding_tax,
			amount_net_due,
			amount,
			due_date,
			settled_date,
			check_number,
			date_received,
			or_number,
			recipientType,
			recipientId,
			client,
			project,
			unit,
			payment,
			created_at,
			updated_at
		} = commission || {};

		const paymentStatusOptions = paymentStatuses.map((status) => {
			return {
				label: formatPaymentStatus(status),
				value: status
			}
		})


        return (
            <>
                <div className="content">
					<Row>
						<Col md="12">
							<Card>
								<CardHeader className="mb-5">
									<CardTitle tag="h3">
										Edit Commission
                  					</CardTitle>
									<Button
										className="float-right"
										color="default"
										onClick={() => this.goTo('/commissions')}
									>
										Back
                  					</Button>
                                </CardHeader>
                                <CardBody>
								{!commission
										? <div>Loading...</div>
										: <>
												{/* Read-only Information Section */}
												<h5 className="mb-4">Commission Information</h5>
												
												<Row>
													<Col md="12">
														<FormGroup>
															<Label>Client</Label>
															<Input
																type="text"
																value={client ? `${client.first_name} ${client.last_name}` : 'N/A'}
																readOnly
															/>
														</FormGroup>
													</Col>
													
												</Row>

												<Row>
													<Col md="6">
														<FormGroup>
															<Label>Recipient Type</Label>
															<Input
																type="text"
																value={recipientType ? recipientType.charAt(0).toUpperCase() + recipientType.slice(1) : 'N/A'}
																readOnly
															/>
														</FormGroup>
													</Col>
													<Col md="6">
														<FormGroup>
															<Label>Recipient</Label>
															<Input
																type="text"
																value={recipientId 
																	? (recipientType === 'broker' 
																		? recipientId.name 
																		: `${recipientId.first_name} ${recipientId.last_name}`)
																	: 'N/A'
																}
																readOnly
															/>
														</FormGroup>
													</Col>
												</Row>

												{project && <FormGroup>
													<Label>Project</Label>
													<Input
														type="text"
														value={project.name || 'N/A'}
														readOnly
													/>
												</FormGroup>}

												{unit && <FormGroup>
													<Label>Unit</Label>
													<Input
														type="text"
														value={unit.block_number && unit.lot_number 
															? `Block ${unit.block_number}, Lot ${unit.lot_number}`
															: (unit.lot_number || unit.block_number || 'N/A')
														}
														readOnly
													/>
												</FormGroup>}

												{payment && <FormGroup>
													<Label>Related Payment</Label>
													<Input
														type="text"
														value={payment._id || 'N/A'}
														readOnly
													/>
												</FormGroup>}

												<FormGroup>
													<Label>Commissionable Amount</Label>
													<CurrencyInput 
														name="commissionable_amount"
														price={commissionable_amount || 0}
														readOnly
														placeholder="Commissionable Amount"
													/>
												</FormGroup>
												<FormGroup>
													<Label>Cleared Percentage</Label>
													<Input
														type="text"
														name="cleared_percentage"
														value={`${cleared_percentage ? (cleared_percentage * 100).toFixed(2) : '0'}%`}
														readOnly
													/>
												</FormGroup>

												<FormGroup>
													<Label>Amount <span className="required-start">*</span></Label>
													<CurrencyInput 
														name="amount"
														price={amount || 0}
														readOnly
														placeholder="Commission Amount"
													/>
												</FormGroup>

												<FormGroup>
													<Label>VAT Amount</Label>
													<CurrencyInput 
														name="vat_amount"
														price={vat_amount || 0}
														readOnly
														placeholder="VAT Amount"
													/>
												</FormGroup>
												<FormGroup>
													<Label>Withholding Tax</Label>
													<CurrencyInput 
														name="withholding_tax"
														price={withholding_tax || 0}
														readOnly
														placeholder="Withholding Tax"
													/>
												</FormGroup>

												<FormGroup>
													<Label>Net Due</Label>
													<CurrencyInput 
														name="amount_net_due"
														price={amount_net_due || 0}
														readOnly
														placeholder="Net Due"
													/>
												</FormGroup>

												<hr className="my-4" />

												{/* Editable Fields Section */}
												<h5 className="mb-4">Editable Fields</h5>

												<FormGroup>
													<Label>Status <span className="required-start">*</span></Label>
													<Select
														value={selectedStatus}
														name="selectedStatus"
														options={paymentStatusOptions}
														className="basic-multi-select"
														classNamePrefix="select"
														onChange={this.onSelectChange('selectedStatus')}
													/>
												</FormGroup>

												<Row>
													<Col sm='6'>
														<FormGroup>
															<Label>Due Date <span className="required-start">*</span></Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Due Date",
																	name: "due_date"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof due_date === 'string' ? moment(due_date).format("MM/DD/YYYY") : due_date}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "due_date" }})}
																closeOnSelect={true}
															/>
														</FormGroup>
													</Col>
													<Col sm='6'>
														<FormGroup>
															<Label>Settled Date</Label>
															<Datetime
																inputProps={{
																	className: "form-control",
																	placeholder: "Settled Date",
																	name: "settled_date"
																}}
																dateFormat="MM/DD/YYYY"
																timeFormat={false}
																value={typeof settled_date === 'string' ? moment(settled_date).format("MM/DD/YYYY") : settled_date}
																onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "settled_date" }})}
																closeOnSelect={true}
															/>
														</FormGroup>
													</Col>
												</Row>

												<FormGroup>
													<Label>Check Number</Label>
													<Input
														type="text"
														name="check_number"
														value={check_number || ''}
														onChange={this.onChangeField}
														placeholder="Leave blank if payment method is CASH"
													/>
												</FormGroup>

												{!!check_number && <FormGroup>
													<Label>Date Received</Label>
													<Datetime
														inputProps={{
															className: "form-control",
															placeholder: "Date Received",
															name: "date_received"
														}}
														dateFormat="MM/DD/YYYY"
														timeFormat={false}
														value={typeof date_received === 'string' ? moment(date_received).format("MM/DD/YYYY") : date_received}
														onChange={(date) => this.onChangeField({target: { value: moment(date).format("MM/DD/YYYY"), name: "date_received" }})}
														closeOnSelect={true}
													/>
												</FormGroup>}

												<FormGroup>
													<Label>OR Number</Label>
													<Input
														type="text"
														name="or_number"
														value={or_number || ''}
														onChange={this.onChangeField}
														placeholder="Official Receipt Number"
													/>
												</FormGroup>

												<hr className="my-4" />

												{_id 
										? 	<>
												<FormGroup>
													<Label>Created At</Label>
													<Input
														type="text"
														name="created_at"
														value={getFormattedDate(created_at || '')}
														readOnly
													/>
												</FormGroup>
												<FormGroup>
													<Label>Updated At</Label>
													<Input
														type="text"
														name="updated_at"
														value={getFormattedDate(updated_at || '')}
														readOnly
													/>
												</FormGroup>
											</> 
										: null}
										{error ? <div className="alert alert-danger mt-3">
												<span>{error}</span>
											</div> : null
										}
									{commission ? <Col>
										<Button color="primary" block={false} className="mb-4" onClick={this.save} disabled={saving}>
											{saving
												? "Saving..."
												: "Save Changes"
											}
										</Button>
									</Col> : null}
											</>
									}												
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </>
        )
    }
}

export default CommissionForm;